import { useEffect, useRef, useState } from "react";

export const getEditorElement = (contentRef, containerRef) => {
  if (contentRef) {
    return contentRef;
  }

  if (!containerRef) {
    return null;
  }

  return containerRef.querySelector('[contenteditable="true"]');
};

export const storeSelection = (range, lastSelectionRef) => {
  if (!range || !lastSelectionRef) {
    return;
  }

  try {
    lastSelectionRef.current = range.cloneRange();
  } catch (error) {
    lastSelectionRef.current = null;
  }
};

export const restoreSelection = (editor, lastSelectionRef) => {
  if (!editor) {
    return null;
  }

  const selection = window.getSelection();
  if (!selection) {
    return null;
  }

  selection.removeAllRanges();
  if (lastSelectionRef?.current) {
    try {
      const storedRange = lastSelectionRef.current;
      
      // First, try to use the stored range directly if it's still valid
      try {
        // Check if the stored range's containers are still in the DOM and within the editor
        const startContainer = storedRange.startContainer;
        const endContainer = storedRange.endContainer;
        
        // Verify containers are still valid and within editor
        if (editor.contains(startContainer) && editor.contains(endContainer)) {
          // Try to restore the exact range
          const newRange = document.createRange();
          newRange.setStart(startContainer, storedRange.startOffset);
          newRange.setEnd(endContainer, storedRange.endOffset);
          
          // Verify the range is valid
          if (newRange.startContainer === startContainer && 
              newRange.endContainer === endContainer) {
            selection.addRange(newRange);
            return selection;
          }
        }
      } catch (e) {
        // Range is invalid, continue to fallback logic
      }
      
      // If direct restoration failed, try to find the equivalent position
      // Calculate text offset from start of editor to cursor position
      const preRange = document.createRange();
      preRange.selectNodeContents(editor);
      preRange.setEnd(storedRange.startContainer, storedRange.startOffset);
      const textOffset = preRange.toString().length;
      
      // Find the equivalent text position in current DOM
      const walker = document.createTreeWalker(
        editor,
        NodeFilter.SHOW_TEXT,
        null
      );
      
      let currentOffset = 0;
      let targetNode = null;
      let targetOffset = 0;
      
      let node;
      while ((node = walker.nextNode())) {
        const nodeLength = node.textContent.length;
        if (currentOffset + nodeLength >= textOffset) {
          targetNode = node;
          targetOffset = textOffset - currentOffset;
          break;
        }
        currentOffset += nodeLength;
      }
      
      if (targetNode) {
        const newRange = document.createRange();
        newRange.setStart(
          targetNode,
          Math.min(targetOffset, targetNode.textContent.length)
        );
        newRange.collapse(true);
        selection.addRange(newRange);
        return selection;
      }
      
      // If we can't find the exact position using text offset, check if stored range is at editor level
      if (storedRange.startContainer === editor || 
          (storedRange.startContainer.nodeType !== Node.TEXT_NODE && 
           storedRange.startContainer.parentElement === editor)) {
        // Get all child nodes
        const children = Array.from(editor.childNodes);
        const childIndex = storedRange.startOffset;
        
        // If childIndex is within children range, find the appropriate text position
        if (childIndex < children.length) {
          const targetElement = children[childIndex];
          
          // If target element is a text node, use it directly
          if (targetElement.nodeType === Node.TEXT_NODE) {
            const newRange = document.createRange();
            // For text nodes, offset refers to character position
            newRange.setStart(targetElement, Math.min(storedRange.startOffset, targetElement.textContent.length));
            newRange.collapse(true);
            selection.addRange(newRange);
            return selection;
          }
          
          // If target element is a block element (p, div, etc.), find the appropriate position
          // Check if it's empty or has content
          if (targetElement.textContent.trim().length === 0) {
            // Empty element - place cursor at start
            const newRange = document.createRange();
            newRange.setStart(targetElement, 0);
            newRange.collapse(true);
            selection.addRange(newRange);
            return selection;
          } else {
            // Element has content - find first text node or place at start
            const walker = document.createTreeWalker(
              targetElement,
              NodeFilter.SHOW_TEXT,
              null
            );
            const firstTextNode = walker.nextNode();
            if (firstTextNode) {
              const newRange = document.createRange();
              newRange.setStart(firstTextNode, 0);
              newRange.collapse(true);
              selection.addRange(newRange);
              return selection;
            } else {
              // No text node found, place cursor at start of element
              const newRange = document.createRange();
              newRange.setStart(targetElement, 0);
              newRange.collapse(true);
              selection.addRange(newRange);
              return selection;
            }
          }
        } else if (childIndex === children.length) {
          // Cursor is at the end of editor - find last text node or create position at end
          if (children.length > 0) {
            const lastChild = children[children.length - 1];
            if (lastChild.nodeType === Node.TEXT_NODE) {
              const newRange = document.createRange();
              newRange.setStart(lastChild, lastChild.textContent.length);
              newRange.collapse(true);
              selection.addRange(newRange);
              return selection;
            } else {
              // Last child is an element, find last text node inside it
              const walker = document.createTreeWalker(
                lastChild,
                NodeFilter.SHOW_TEXT,
                null
              );
              let lastTextNode = null;
              let node;
              while ((node = walker.nextNode())) {
                lastTextNode = node;
              }
              if (lastTextNode) {
                const newRange = document.createRange();
                newRange.setStart(lastTextNode, lastTextNode.textContent.length);
                newRange.collapse(true);
                selection.addRange(newRange);
                return selection;
              }
            }
          }
        }
      }
    } catch (error) {
      console.warn("Error restoring selection:", error);
      selection.removeAllRanges();
    }
  }

  // Fallback: place cursor at end
  const range = document.createRange();
  range.selectNodeContents(editor);
  range.collapse(false);
  selection.addRange(range);
  storeSelection(range, lastSelectionRef);

  return selection;
};

export const useCommonPlaceholderAndEditorRefs = () => {
  const [placeholderDropdownOpen, setPlaceholderDropdownOpen] = useState({});
  const editorContainerRefs = useRef({});
  const editorContentRefs = useRef({});

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      Object.keys(placeholderDropdownOpen).forEach((key) => {
        const dropdownElement = document.getElementById(
          `placeholder-dropdown-${key}`
        );
        const buttonElement = document.getElementById(
          `placeholder-button-${key}`
        );
        if (
          dropdownElement &&
          buttonElement &&
          !dropdownElement.contains(event.target) &&
          !buttonElement.contains(event.target) &&
          buttonElement !== event.target
        ) {
          setPlaceholderDropdownOpen((prev) => ({ ...prev, [key]: false }));
        }
      });
    };

    if (
      Object.keys(placeholderDropdownOpen).some(
        (key) => placeholderDropdownOpen[key]
      )
    ) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [placeholderDropdownOpen]);
  return {
    placeholderDropdownOpen,
    setPlaceholderDropdownOpen,
    editorContainerRefs,
    editorContentRefs,
  };
};
